import {
  FrequencyUnit,
  Timestamp,
  UpcomingPeriod,
  UpcomingPeriodTypes,
  WeekDays,
} from '@griply/types';
import {
  addMonths,
  addWeeks,
  endOfWeek,
  format,
  isSameMonth,
  isSameWeek,
  isSameYear,
  startOfWeek,
} from 'date-fns';

import { weekdaysNumberMap } from '../constants';
import { getStartTillEndLabel } from '../get-start-till-end-label';
import { TranslateFunction } from '../get-task-due-date';

export type GetPeriodLabelOptions = {
  period: UpcomingPeriod;
  today: Timestamp;
  weekStartsOn: WeekDays;
  dateFormat?: string;
  t: TranslateFunction;
};

export const getPeriodLabel = ({
  period,
  today,
  weekStartsOn,
  dateFormat,
  t,
}: GetPeriodLabelOptions): string => {
  if (period.type === UpcomingPeriodTypes.Overdue) {
    return t('general.labels.overdue');
  }

  const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
  const periodDate = period.date;

  if (period.type === FrequencyUnit.Month) {
    if (isSameMonth(periodDate, today)) {
      return t('dialogs.task-detail.meta.upcoming.this-month.label');
    }

    if (isSameMonth(periodDate, addMonths(today, 1))) {
      return t('dialogs.task-detail.meta.upcoming.next-month.label');
    }

    if (isSameYear(periodDate, today)) {
      return format(periodDate, 'MMMM');
    }

    return format(periodDate, 'MMMM yyyy');
  }

  if (period.type === FrequencyUnit.Year) {
    return format(periodDate, 'yyyy');
  }

  if (isSameWeek(periodDate, today, dateFnsOptions)) {
    return t('dialogs.task-detail.meta.upcoming.this-week.label');
  }

  if (isSameWeek(periodDate, addWeeks(today, 1), dateFnsOptions)) {
    return t('dialogs.task-detail.meta.upcoming.next-week.label');
  }

  return getStartTillEndLabel({
    startDate: startOfWeek(periodDate, dateFnsOptions),
    endDate: endOfWeek(periodDate, dateFnsOptions),
    today,
    t,
    dateFormat,
  });
};
